/**
 * PWA インストール促進。
 *
 * beforeinstallprompt イベントを捕捉し、ユーザーに明示的に
 * 「ホーム画面に追加」を促すボタンを表示する。
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export class PwaInstallPrompt {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly container: HTMLElement;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'pwa-install-prompt';
    this.container.setAttribute('role', 'status');
    this.container.setAttribute('aria-live', 'polite');
    this.container.dataset.visible = 'false';

    this.container.innerHTML = `
      <span class="pwa-install-prompt__text">📲 ホーム画面に追加するとアプリのように遊べます</span>
      <button type="button" class="btn btn--primary pwa-install-prompt__btn" aria-label="ホーム画面に追加する">追加</button>
      <button type="button" class="btn btn--ghost pwa-install-prompt__close" aria-label="閉じる">✕</button>
    `;

    this.container.querySelector('.pwa-install-prompt__btn')?.addEventListener('click', () => {
      void this.promptInstall();
    });
    this.container.querySelector('.pwa-install-prompt__close')?.addEventListener('click', () => {
      this.hide();
    });

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.show();
    });

    // すでにstandaloneで起動している場合は表示しない
    if (window.matchMedia('(display-mode: standalone)').matches) {
      this.container.dataset.visible = 'false';
    }
  }

  mount(parent: HTMLElement): void {
    parent.append(this.container);
  }

  private show(): void {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    this.container.dataset.visible = 'true';
  }

  private hide(): void {
    this.container.dataset.visible = 'false';
    this.deferredPrompt = null;
  }

  private async promptInstall(): Promise<void> {
    if (!this.deferredPrompt) return;
    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      this.hide();
    }
  }
}
